import {
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatExpIconButton } from '@camaradesuk/m3e-angular';
import { filter, map } from 'rxjs';
import { breakpointMap, breakpoints } from '@shared/layout/break-points';
import { NotificationInboxService } from './notification-inbox.service';
import { NotificationPanelComponent } from './notification-panel/notification-panel.component';

/** The desktop panel's width and greatest height (prototype `.pop`: 400 px, at most 640 px). */
export const PANEL_WIDTH_PX = 400;
export const PANEL_MAX_HEIGHT_PX = 640;
/** The gap kept between the panel and the window's edges on desktop. */
const PANEL_MARGIN_PX = 8;

/** Below the bell, its right edge aligned with the bell's; flipped above only if there is no room below. */
const PANEL_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    offsetY: 4,
  },
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 4,
  },
];

/**
 * The app bar's bell. The unread number shows only as a Material 3 badge over the bell's top-right (large badge: 16 px
 * high at least, label small, error roles, "99+" past 99) and is absent at zero; the button's name carries the number
 * for screen readers (N3, Option D, Chris 28 October 2026). The badge is still a small span on M3 roles from before
 * `.syrf-m3-badge` gave `matBadge` its M3 look (#4755, which the Unread tab uses); moving it onto that class is #4760.
 *
 * Selecting the bell opens the compact bell panel (N4) anchored below it; on a phone (below 600 px) the same panel is
 * a full-width sheet from the bottom of the app bar to the bottom of the screen. Escape, a click or tap outside and
 * the bell itself close it and return focus to the bell; any navigation (a card's link, "See all in Notifications",
 * Email preferences) closes it and leaves focus to the new page.
 */
@Component({
  selector: 'syrf-notification-inbox-button',
  // Expressive buttons before the flag (#4769): keeps the m3e look whatever expressiveButtons says.
  host: { class: 'syrf-expressive-buttons' },
  imports: [
    MatIconButton,
    MatExpIconButton,
    MatIcon,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NotificationPanelComponent,
  ],
  template: `<button
      #bell
      matIconButton
      matExpIconButton
      type="button"
      class="bell"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      aria-haspopup="dialog"
      [attr.aria-expanded]="open()"
      [attr.aria-label]="label()"
      (click)="toggle()"
    >
      <span class="anchor">
        <mat-icon aria-hidden="true">notifications</mat-icon>
        @if (badge(); as text) {
          <span class="badge" data-slot="badge" aria-hidden="true">{{
            text
          }}</span>
        }
      </span>
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="open()"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayPush]="true"
      [cdkConnectedOverlayViewportMargin]="compact() ? 0 : margin"
      [cdkConnectedOverlayWidth]="width()"
      [cdkConnectedOverlayHeight]="height()"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      cdkConnectedOverlayPanelClass="syrf-notification-panel-pane"
      (backdropClick)="close(true)"
      (overlayKeydown)="onKeydown($event)"
      (detach)="close(false)"
    >
      <syrf-notification-panel
        [compact]="compact()"
        [style.max-height]="maxHeight()"
        (closeRequested)="close(true)"
      />
    </ng-template>`,
  styles: `
    :host {
      display: inline-flex;
    }
    .bell {
      color: inherit;
    }
    .anchor {
      position: relative;
      display: inline-grid;
      place-items: center;
    }
    /* Material 3 large badge: 16 px high, at least 16 px wide, label small, error roles; it overlaps the icon's
       top-right corner (offset from the 24 px icon as in the M3 spec). */
    .badge {
      position: absolute;
      /* Above the icon: Material lifts an icon button's mat-icon to z-index 1, which drew the bell over its badge. */
      z-index: 2;
      top: -4px;
      left: 12px;
      box-sizing: border-box;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 8px;
      display: inline-grid;
      place-items: center;
      background: var(--mat-sys-error);
      color: var(--mat-sys-on-error);
      font: var(--mat-sys-label-small);
      font-size: 11px;
      line-height: 16px;
      white-space: nowrap;
      pointer-events: none;
    }
  `,
})
export class NotificationInboxButtonComponent {
  readonly inbox = inject(NotificationInboxService);
  private readonly _bell = viewChild.required('bell', {
    read: ElementRef<HTMLElement>,
  });

  protected readonly positions = PANEL_POSITIONS;
  protected readonly margin = PANEL_MARGIN_PX;

  /** The panel is open. */
  readonly open = signal(false);
  /** Where the bell's bottom edge was when the panel opened (the phone sheet starts there). */
  private readonly _top = signal(0);

  /** Phone width (below 600 px): the panel is a full-width sheet. */
  readonly compact = toSignal(
    inject(BreakpointObserver)
      .observe(breakpointMap[breakpoints.lt_sm].mediaQuery)
      .pipe(map((state) => state.matches)),
    { initialValue: false }
  );

  protected readonly width = computed(() =>
    this.compact()
      ? '100vw'
      : `min(${PANEL_WIDTH_PX}px, calc(100vw - ${2 * PANEL_MARGIN_PX}px))`
  );
  /** The phone sheet reaches the bottom of the screen; the desktop panel takes the height its rows need. */
  protected readonly height = computed(() =>
    this.compact() ? `calc(100dvh - ${this._top() + 4}px)` : ''
  );
  readonly maxHeight = computed(() =>
    this.compact()
      ? ''
      : `min(${PANEL_MAX_HEIGHT_PX}px, calc(100dvh - ${this._top() + 4 + PANEL_MARGIN_PX}px))`
  );

  /** "3", "99+", or nothing at zero. */
  protected readonly badge = computed(() => {
    if (this.inbox.unreadCountCapped()) return '99+';
    const count = this.inbox.unreadCount();
    return count > 0 ? String(count) : null;
  });

  protected readonly label = computed(
    () =>
      `Notifications, ${this.inbox.unreadCountCapped() ? '99+' : this.inbox.unreadCount()} unread`
  );

  constructor() {
    // Any navigation (a card's link, See all, Email preferences, a snackbar's Open project) closes the panel; the new
    // page takes focus, so it does not return to the bell.
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => this.close(false));
  }

  toggle(): void {
    if (this.open()) {
      this.close(true);
      return;
    }
    this._top.set(this._bell().nativeElement.getBoundingClientRect().bottom);
    this.open.set(true);
  }

  /** Closes the panel; `refocus` returns focus to the bell (Escape, outside click, the bell itself). */
  close(refocus: boolean): void {
    if (!this.open()) return;
    this.open.set(false);
    if (refocus) this._bell().nativeElement.focus();
  }

  /**
   * Escape closes the panel. The CDK sends keys only to the topmost overlay, so while the options menu or a dialog is
   * open over the panel, Escape reaches that first and closes only it.
   */
  protected onKeydown(event: KeyboardEvent): void {
    // The CDK also detaches the overlay on Escape (it reads the legacy keyCode); closing here first returns focus.
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close(true);
  }
}
